import { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Users, Inbox, PenTool, KeyRound, Image as ImageIcon, ClipboardList, ScrollText,
} from 'lucide-react';
import { useAdminQueues } from '../context/AdminQueueContext.jsx';
import { useAuth } from '../context/AuthContext.jsx';

// Chrome for every /admin/* route — the "Signal Rail": a fixed left rail of
// sections carrying live queue badges, plus a breadcrumb strip whose right side
// is a slot for the page's own toolbar.
//
// Replaces AdminSubNav, which rendered an identical hero + tab row on all seven
// admin pages (see docs/ADMIN_REFACTOR_BRIEF.md §5.5/§5.6). Pages now supply
// their own <h1> and description.
//
// Admin routes render through `<Layout bare>`: Layout's centred .container caps
// content near 1320px, which a fixed rail plus a wide table cannot live inside.
// Bare mode keeps the sticky navbar and drops the container + footer, so the
// footer is recreated here, inside the content column.

const ITEMS = [
  { to: '/admin', label: 'Command Deck', icon: LayoutDashboard, end: true },
  { to: '/admin/users', label: 'Users', icon: Users, badge: 'users' },
  { to: '/admin/access-requests', label: 'Access Requests', icon: Inbox, badge: 'access' },
  { to: '/admin/design-requests', label: 'Design Requests', icon: PenTool, badge: 'design' },
  { to: '/admin/grants', label: 'Grants', icon: KeyRound },
  { to: '/admin/package-art', label: 'Package Art', icon: ImageIcon },
  { to: '/admin/registration', label: 'Registration', icon: ClipboardList },
  { to: '/admin/audit-log', label: 'Audit Log', icon: ScrollText },
];

function RailBadge({ kind, userCount }) {
  const { accessPending, designPending } = useAdminQueues();

  if (kind === 'users') {
    if (userCount == null) return null;
    return <span className="adm-rail__count">{userCount}</span>;
  }

  const pending = kind === 'access' ? accessPending : designPending;
  // Zero pending renders nothing: a grey "0" is noise pretending to be signal.
  if (!pending) return null;
  return <span className="adm-rail__count adm-rail__count--amber">{pending}</span>;
}

function Rail({ userCount }) {
  const { user } = useAuth();

  return (
    <nav className="adm-rail" aria-label="Admin sections">
      <div className="adm-rail__eyebrow adm-rv" style={{ animationDelay: '60ms' }}>
        Operator Console
      </div>
      <div className="adm-rail__nav">
        {ITEMS.map(({ to, label, icon: Icon, end, badge }, i) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            className={({ isActive }) => `adm-rail__row adm-rv${isActive ? ' is-active' : ''}`}
            style={{ animationDelay: `${100 + i * 40}ms` }}
            title={label}
          >
            <span className="adm-rail__marker" aria-hidden="true" />
            <Icon className="adm-rail__icon" size={15} aria-hidden="true" />
            <span className="adm-rail__label">{label}</span>
            {badge && <RailBadge kind={badge} userCount={userCount} />}
          </NavLink>
        ))}
      </div>
      <div className="adm-rail__foot adm-rv" style={{ animationDelay: '360ms' }}>
        <div className="adm-rail__user">
          <span className="nav-avatar adm-rail__avatar" aria-hidden="true">
            {user?.name?.[0]?.toUpperCase() ?? '?'}
          </span>
          <span className="adm-rail__usermeta">
            <span className="adm-rail__username">{user?.name}</span>
            <span className="role-tag role-tag--admin adm-rail__userrole">{user?.role}</span>
          </span>
        </div>
        <div className="adm-rail__version">StreamGG Admin</div>
      </div>
    </nav>
  );
}

export default function AdminShell({ crumb, actions, stripStatus = 'Live', userCount, children }) {
  // The navbar's height is styling-dependent (padding, font metrics), so measure
  // it rather than hardcoding — the rail's `top` and the content column's
  // min-height both hang off --adm-nav-h.
  useEffect(() => {
    const nav = document.querySelector('#root > header');
    if (!nav) return undefined;
    const apply = () => {
      const h = nav.offsetHeight;
      if (h) document.documentElement.style.setProperty('--adm-nav-h', `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(nav);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--adm-nav-h');
    };
  }, []);

  return (
    <>
      <Rail userCount={userCount} />
      <main className="adm-main">
        <div className="adm-strip adm-rv" style={{ animationDelay: '200ms' }}>
          <span className="adm-strip__crumb">
            Admin <span className="sep">/</span> <span className="here">{crumb}</span>
          </span>
          {actions && <div className="adm-strip__actions">{actions}</div>}
          <span className="adm-strip__live">
            <span className="adm-dot adm-dot--ok adm-dot--pulse" aria-hidden="true" />
            {stripStatus}
          </span>
        </div>

        <div className="adm-page">{children}</div>

        <footer className="border-top footer text-muted">
          <div className="container py-2" style={{ textAlign: 'center' }}>
            <span>
              &copy; 2026 <span className="text-neon-magenta">STREAM</span>
              <span className="text-neon-blue">GG</span> &middot; Data from{' '}
              <a href="https://start.gg" target="_blank" rel="noreferrer">start.gg</a>
            </span>
          </div>
        </footer>
      </main>
    </>
  );
}
